import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaAward, 
  FaUsers, 
  FaBox,
  FaDrumstickBite,
  FaLeaf,
  FaArrowRight
} from 'react-icons/fa';
import HeroSection from '../components/HeroSection';
import PortraitGallerySection from '../components/PortraitGallerySection';
import StatsSection from '../components/StatsSection';
import ServicesSection from '../components/ServicesSection';
import ContactSection from '../components/ContactSection';

const HomeContainer = styled.div`
  padding-top: 120px;
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: var(--secondary-color);
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

const WhyChooseUsSection = styled(Section)`
  background: var(--light-gray);
`;

const WhyChooseUsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
`;

const WhyChooseUsCard = styled(motion.div)`
  background: var(--white-color);
  padding: 40px 30px;
  border-radius: var(--border-radius);
  text-align: center;
  box-shadow: var(--shadow);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const CardIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: var(--white-color);
`;

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--secondary-color);
`;

const CardDescription = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
  opacity: 0.8;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
`;

const ServiceCard = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: var(--transition);
  }
  
  ${ServiceCard}:hover & img {
    transform: scale(1.1);
  }
`;

const ServiceContent = styled.div`
  padding: 30px;
`;

const ServiceTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--secondary-color);
`;

const ServiceDescription = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
  margin-bottom: 20px;
  opacity: 0.8;
`;

const ServiceButton = styled(motion.button)`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 12px 25px;
  border-radius: var(--border-radius);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
  }
`;

const CTAButton = styled(motion.button)`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 15px 30px;
  border-radius: var(--border-radius);
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 40px auto 0;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
    transform: translateY(-2px);
  }
`;

// Kurban Sıraları Component'i
const SacrificeQueueSection = ({ sacrificeData }) => {
  const QueueCard = styled(motion.div)`
    background: var(--white-color);
    padding: 30px;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    text-align: center;
    transition: var(--transition);
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    }
  `;

  const QueueIcon = styled.div`
    width: 60px;
    height: 60px;
    background: var(--primary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 15px;
    font-size: 24px;
    color: var(--white-color);
  `;

  const QueueNumber = styled.div`
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 10px;
  `;

  const QueueLabel = styled.div`
    font-size: 1rem;
    color: var(--secondary-color);
    font-weight: 500;
  `;

  const QueueGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 30px;
    margin-bottom: 40px;
  `;

  return (
    <Section style={{ background: 'var(--light-gray)' }}>
      <Container>
        <SectionTitle>Kurban Sıraları</SectionTitle>
        <SectionSubtitle>Güncel kesim ve teslim sıraları</SectionSubtitle>
        
        <QueueGrid>
          <QueueCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <QueueIcon>🔪</QueueIcon>
            <QueueNumber>{sacrificeData.kesimSira || '0'}</QueueNumber>
            <QueueLabel>Kesim Sırası</QueueLabel>
          </QueueCard>

          <QueueCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <QueueIcon>🍽️</QueueIcon>
            <QueueNumber>{sacrificeData.teslimSira1 || '0'}</QueueNumber>
            <QueueLabel>Teslim Sırası 1. Masa</QueueLabel>
          </QueueCard>

          <QueueCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <QueueIcon>🍽️</QueueIcon>
            <QueueNumber>{sacrificeData.teslimSira2 || '0'}</QueueNumber>
            <QueueLabel>Teslim Sırası 2. Masa</QueueLabel>
          </QueueCard>

          <QueueCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <QueueIcon>🍽️</QueueIcon>
            <QueueNumber>{sacrificeData.teslimSira3 || '0'}</QueueNumber>
            <QueueLabel>Teslim Sırası 3. Masa</QueueLabel>
          </QueueCard>

          <QueueCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <QueueIcon>🍽️</QueueIcon>
            <QueueNumber>{sacrificeData.teslimSira4 || '0'}</QueueNumber>
            <QueueLabel>Teslim Sırası 4. Masa</QueueLabel>
          </QueueCard>
        </QueueGrid>
      </Container>
    </Section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [isSacrificeModeActive, setIsSacrificeModeActive] = useState(false);
  const [sacrificeData, setSacrificeData] = useState({
    kesimSira: '',
    teslimSira1: '',
    teslimSira2: '',
    teslimSira3: '',
    teslimSira4: ''
  });

  useEffect(() => {
    // LocalStorage'dan kurban modu bilgilerini yükle
    const savedSacrificeMode = localStorage.getItem('sacrificeMode');
    if (savedSacrificeMode) {
      const parsed = JSON.parse(savedSacrificeMode);
      setIsSacrificeModeActive(parsed.isActive);
      setSacrificeData(parsed.data || {
        kesimSira: '',
        teslimSira1: '',
        teslimSira2: '',
        teslimSira3: '',
        teslimSira4: ''
      });
    }
  }, []);
  const whyChooseUs = [
    {
      icon: <FaAward />,
      title: 'Kalite Garantisi',
      description: 'En yüksek kalitede hayvan yetiştirme ve bakım hizmeti'
    },
    {
      icon: <FaUsers />,
      title: 'Uzman Ekip',
      description: 'Deneyimli veteriner ve bakım personeli ile profesyonel hizmet'
    },
    {
      icon: <FaBox />,
      title: 'Güvenilir Hizmet',
      description: 'Müşteri memnuniyeti odaklı, güvenilir ve şeffaf hizmet'
    }
  ];

  const services = [
    {
      icon: <FaDrumstickBite />,
      title: 'Sığır Besiciliği',
      description: 'Profesyonel sığır yetiştirme ve bakım hizmeti',
      image: '/images/services/sigir-besiciligi.jpg'
    },
    {
      icon: <FaLeaf />,
      title: 'Koyun Besiciliği',
      description: 'Geleneksel yöntemlerle koyun yetiştirme',
      image: '/images/services/koyun-besiciligi.jpg'
    }
  ];

  return (
    <HomeContainer>
      {isSacrificeModeActive ? (
        <SacrificeQueueSection sacrificeData={sacrificeData} />
      ) : (
        <>
          <HeroSection />
          <PortraitGallerySection />
        </>
      )}
      
      <Section>
        <Container>
          <SectionTitle>Neden SULTANLAR BESİCİLİK?</SectionTitle>
          <SectionSubtitle>
            Kaliteli hayvan yetiştirme ve satışında 25 yılı aşkın tecrübemizle 
            müşterilerimize en iyi hizmeti sunuyoruz.
          </SectionSubtitle>
          
          <WhyChooseUsGrid>
            {whyChooseUs.map((item, index) => (
              <WhyChooseUsCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <CardIcon>{item.icon}</CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </WhyChooseUsCard>
            ))}
          </WhyChooseUsGrid>
        </Container>
      </Section>
      
      <ServicesSection />
      
      <StatsSection />
      
      <WhyChooseUsSection>
        <Container>
          <SectionTitle>Hizmetlerimiz</SectionTitle>
          <SectionSubtitle>
            Besi çiftliğimizde sunduğumuz kapsamlı hizmetler ile 
            müşterilerimizin tüm ihtiyaçlarını karşılıyoruz.
          </SectionSubtitle>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ServiceImage>
                  <img src={service.image} alt={service.title} />
                </ServiceImage>
                <ServiceContent>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <ServiceButton
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/hizmetler')}
                  >
                    Hizmete Başla
                    <FaArrowRight />
                  </ServiceButton>
                </ServiceContent>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </WhyChooseUsSection>
      
      <ContactSection />
    </HomeContainer>
  );
};

export default HomePage; 